import { useEffect, useRef } from "react";
import { SESSION_TIMEOUT_MS } from "../utils/config";

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
] as const;

/**
 * Calls `onIdle` when the user has had no activity for `IDLE_TIMEOUT_MS`.
 * The timer resets every time the user interacts with the page.
 * When `enabled` is false (e.g. user is not logged in), the timer is inactive.
 */
export function useIdleTimer(onIdle: () => void, enabled: boolean) {
  const onIdleRef = useRef(onIdle);
  useEffect(() => {
    onIdleRef.current = onIdle;
  });

  useEffect(() => {
    if (!enabled) return;

    let timer: number;

    function resetTimer() {
      clearTimeout(timer);
      timer = window.setTimeout(() => {
        onIdleRef.current();
      }, SESSION_TIMEOUT_MS);
    }

    resetTimer();

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, resetTimer, { passive: true })
    );

    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, resetTimer)
      );
    };
  }, [enabled]);
}
